/**
 * CountrySelectorPopup
 *
 * A modal shown on first visit that prompts the user to choose their
 * shipping country. Once dismissed or a country is selected, a flag in
 * localStorage prevents it from appearing again.
 *
 * Visibility logic:
 * - Show when: no `preferred_locale` cookie AND no `country_popup_seen` in localStorage
 * - Hide when: user selects a country OR clicks dismiss / backdrop
 */

import styled from '@emotion/styled';
import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Modal, ModalHeader, ModalBody } from '@template-project/afrodita-components';
import { useIntl, getCountryGroups, getLanguageLabel } from '@/lib/intl';
import { getCookie } from '@/lib/intl/IntlProvider';
import { countryCodeToFlagEmoji } from '@/components/CountrySelector/CountrySelector';
import type { CountryConfig } from '@template-project/storefront-shared';
import { useTranslation } from '@/lib/i18n';
import {
  COUNTRY_SELECTOR_POPUP_SUBTITLE,
  COUNTRY_SELECTOR_POPUP_TITLE,
} from './i18.keys';

const POPUP_SEEN_KEY = 'country_popup_seen';
const PREFERRED_LOCALE_COOKIE = 'preferred_locale';

function hasSeenPopup(): boolean {
  try {
    return localStorage.getItem(POPUP_SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

function markPopupSeen(): void {
  try {
    localStorage.setItem(POPUP_SEEN_KEY, '1');
  } catch {
    // localStorage may be unavailable (private browsing, etc.)
  }
}

const Subtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 24px;
`;

const CountryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  min-height: 0;
`;

const CountryCard = styled.button<{ readonly active: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px 16px;
  background: ${({ active, theme }) =>
    active ? theme.colors.background.secondary : theme.colors.background.primary};
  border: 2px solid
    ${({ active, theme }) => (active ? theme.colors.text.primary : theme.colors.border.color)};
  border-radius: ${({ theme }) => theme.radius.regular};
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, background-color 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.text.secondary};
    background: ${({ theme }) => theme.colors.background.secondary};
  }
`;

const FlagSpan = styled.span`
  font-size: 28px;
  line-height: 1;
`;

const CardDetails = styled.div`
  flex: 1;
`;

const CardCountryName = styled.span`
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CardCurrency = styled.span`
  display: block;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 2px;
`;

const CardLanguages = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

const CardLangButton = styled.button<{ readonly active: boolean }>`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 12px;
  font-weight: ${({ active }) => (active ? 600 : 400)};
  color: ${({ active, theme }) =>
    active ? theme.colors.text.primary : theme.colors.text.secondary};
  text-decoration: ${({ active }) => (active ? 'none' : 'underline')};
  transition: color 0.15s;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export function CountrySelectorPopup() {
  const { t } = useTranslation();
  const { locale, changeLocale } = useIntl();
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  const countryGroups = getCountryGroups();

  // Only show on the homepage — product/collection pages need unblocked interactions
  const isHomePage = location.pathname === '/' || /^\/[a-z]{2}-[A-Z]{2}\/?$/.test(location.pathname);

  useEffect(() => {
    if (!isHomePage) return;
    const hasCookie = Boolean(getCookie(PREFERRED_LOCALE_COOKIE));
    const hasSeen = hasSeenPopup();
    if (!hasCookie && !hasSeen) {
      setVisible(true);
    }
  }, [isHomePage]);

  const handleClose = useCallback(() => {
    markPopupSeen();
    setVisible(false);
  }, []);

  const handleSelect = useCallback(
    (config: CountryConfig) => {
      markPopupSeen();
      if (config.locale !== locale) {
        changeLocale(config.locale);
      }
      setVisible(false);
    },
    [locale, changeLocale]
  );

  return (
    <Modal
      variant="centered"
      size="sm"
      isOpen={visible}
      onClose={handleClose}
      id="country-popup"
    >
      <ModalHeader title={t(COUNTRY_SELECTOR_POPUP_TITLE)} onClose={handleClose} />
      <ModalBody>
        <Subtitle>{t(COUNTRY_SELECTOR_POPUP_SUBTITLE)}</Subtitle>

        <CountryList>
          {countryGroups.map((group) => {
            const isActiveCountry = group.locales.some((l) => l.locale === locale);
            const primaryLocale = group.locales[0];
            const hasMultipleLanguages = group.locales.length > 1;

            return (
              <CountryCard
                key={group.countryCode}
                active={isActiveCountry}
                onClick={hasMultipleLanguages ? undefined : () => handleSelect(primaryLocale)}
                type="button"
                style={hasMultipleLanguages ? { cursor: 'default' } : undefined}
              >
                <FlagSpan>{countryCodeToFlagEmoji(group.countryCode)}</FlagSpan>
                <CardDetails>
                  <CardCountryName>{group.displayName}</CardCountryName>
                  <CardCurrency>
                    {group.currency.format} {group.currency.code}
                  </CardCurrency>
                  {hasMultipleLanguages && (
                    <CardLanguages>
                      {group.locales.map((loc) => (
                        <CardLangButton
                          key={loc.locale}
                          active={loc.locale === locale}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelect(loc);
                          }}
                          type="button"
                        >
                          {getLanguageLabel(loc.language)}
                        </CardLangButton>
                      ))}
                    </CardLanguages>
                  )}
                </CardDetails>
              </CountryCard>
            );
          })}
        </CountryList>
      </ModalBody>
    </Modal>
  );
}
